import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { MediaObserver } from "@angular/flex-layout";

import * as _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService } from "app/auth/service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";
import { locale as english } from "app/utils/i18n/en";
import { locale as french } from "app/utils/i18n/fr";
import { locale as arabic } from "app/utils/i18n/ar";
import { locale as dialecte } from "app/utils/i18n/da";
import { User } from "app/auth/models";

import { coreConfig } from "app/app-config";
import { Router } from "@angular/router";
import { DataServiceService } from "app/service/data-service.service";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { imgavatar } from "app/utils/defavatar";
import { CoreTranslationService } from "@core/services/translation.service";
import { CoreMenuService } from "@core/components/core-menu/core-menu.service";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy, OnChanges {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;
  nbr: string = "0";
  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User;
  public marchand: any;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  avatarImage: SafeResourceUrl;
  @HostBinding("class.fixed-top")
  public isFixed = false;

  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private modalService: NgbModal,
    private dataservice: DataServiceService,
    public sanitizer: DomSanitizer,
    private _coreTranslationService: CoreTranslationService,
    private _coreMenuService: CoreMenuService
  ) {
    this._coreTranslationService.translate(english, french, arabic, dialecte);
    this._authenticationService.currentUser.subscribe((x) => (this.currentUser = x));
    this.dataservice.data$.subscribe((d) => {
      this.nbr = d.unviewedReclamations;
    });
    this.languageOptions = {
      en: {
        title: "English",
        flag: "us",
        tr: "en",
      },
      fr: {
        title: "Francais",
        flag: "fr",
        tr: "fr",
      },
      ar: {
        title: "Arabic",
        flag: "tn",
        tr: "ar",
      },
      tn: {
        title: "Dialecte",
        flag: "tn",
        tr: "da",
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.nbr = changes.data.currentValue;
    }
  }
  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem("prevSkin");

    if (this.currentSkin === "dark") {
      this._coreConfigService.setConfig(
        {
          layout: { skin: this.prevSkin ? this.prevSkin : "default" },
          app: { appLogoImage: "assets/images/logo/logo_spw.png" },
        },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem("prevSkin", this.currentSkin);
      this._coreConfigService.setConfig(
        { layout: { skin: "dark" }, app: { appLogoImage: "assets/images/logo/logo_spw_2.png" } },
        { emitEvent: true }
      );
    }
  }

  /**
   * Logout method
   */
  logout() {
    localStorage.removeItem('marchand');  // remove marchand info
    localStorage.removeItem('display');      // unmark as marchand
    localStorage.removeItem('pin');
    localStorage.removeItem('vC');
    this._authenticationService.logout();
    this._router.navigate(["/auth/login"]);
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    if (localStorage.getItem("marchand")) {
      this.marchand = JSON.parse(localStorage.getItem("marchand"));
    }
    // get the currentUser details from localStorage
    if (localStorage.getItem("currentUser")) {
      this.currentUser = JSON.parse(localStorage.getItem("currentUser"));
      if (this.currentUser.imageProfil) {
        this.avatarImage = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.currentUser.imageProfil
        );
      } else {
        this.avatarImage = this.sanitizer.bypassSecurityTrustResourceUrl(imgavatar);
      }
    }

    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === "horizontal";
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === "vertical") {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === "fixed-top") {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == "horizontal") {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive("bs-gt-xl");
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // modal Open Form
  modalOpenForm(modalForm) {
    this.modalService.open(modalForm);
  }

  displayLang(lang) {
    var v = lang + ".title";
    return this._translateService.instant(v);
  }

  showMarchandMenu(): void {
    localStorage.setItem('display', '1');        // mark as marchand
    window.location.reload();    
  }
}
